import { useEffect, useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { connect, useDispatch } from 'react-redux';

import { Box, IconButton, Tooltip, Typography, useTheme } from '@mui/material';

import AddRole from './addRole';
import PopUp from '../../components/Popup';
import { userRoleActions } from '../../store/reducers/user-role';
import CheckboxComponent from '../../components/Checkbox';
import { RolesDataRequestPayloadTypes, UserRoleFormData } from '../../store/models/user-management';
import DataTable from '../../components/DataTable';

import { ReactComponent as Accountkey } from '../../assets/icons/main/account-key-icon.svg';
import { ReactComponent as AlertIcon } from '../../assets/icons/main/alertIcon.svg';
import { ReactComponent as DeleteIcon } from '../../assets/icons/main/delete-icon.svg';

import { DEFAULT_PAGE_NUMBER, DEFAULT_PAGE_SIZE } from '../../utils/table-utils';
import { palette } from '../../theme/palette';
import { get } from 'lodash';

const Styles = {
  cellText: {
    fontSize: '16px',
    marginLeft: '10px',
    textOverflow: 'ellipsis',
    overflow: 'hidden',

    '&.disabled': {
      opacity: 0.5,
      userSelect: 'none',
    },
  },
};

interface UseCaseTypes {
  isUserRoleLoading: boolean;
  userRole: any;
  userRoleTotalRowCount: number;
  userRolePageNumber: number;
  isAddRoleLoadingSuccess: boolean;
  isDeleteRoleSuccess: boolean;
  isDeleteRoleError: boolean;
  userRoleManagementMessage: string;
  userRoleError: boolean;
  isAddRoleError: boolean;
  // onTabChange: any;
  setAddPopupOpen: any;
  addPopupOpen: any;
  roleIds: any;
  setRoleIds: any;
  confirmPopup: any;
  setConfirmPopup: any;
  setSelectedRows: any;
  selectedRows: any;
  userAccess: any;
  userListAccess: any;
}

const userFormData: UserRoleFormData = {
  name: '',
};

const UserRole = ({
  isUserRoleLoading,
  userRole,
  userRoleTotalRowCount,
  userRolePageNumber,
  isAddRoleLoadingSuccess,
  isDeleteRoleSuccess,
  setAddPopupOpen,
  addPopupOpen,
  roleIds,
  setRoleIds,
  confirmPopup,
  setConfirmPopup,
  setSelectedRows,
  selectedRows,
  userAccess,
  userListAccess,
}: UseCaseTypes) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [userRoleData, setUserRoleData] = useState(userFormData);
  const [sortingModel, setSortingModel] = useState({ field: '', sort: '' });
  const themeData: any = useTheme();
  const dispatchRolesRequest = (payload: RolesDataRequestPayloadTypes) => {
    dispatch(userRoleActions.getRoleList({ ...sortingModel, ...payload, role_table: true }));
  };

  useEffect(() => {
    if (userAccess.read) {
      dispatchRolesRequest({ limit: DEFAULT_PAGE_SIZE, page: DEFAULT_PAGE_NUMBER });
    }
  }, [userAccess.read]);

  useEffect(() => {
    if (isAddRoleLoadingSuccess || isDeleteRoleSuccess) {
      dispatchRolesRequest({
        limit: DEFAULT_PAGE_SIZE,
        page:
          isDeleteRoleSuccess && userRole.length === 1 && userRolePageNumber !== 1
            ? userRolePageNumber - 1
            : userRolePageNumber,
      });
    }
  }, [isAddRoleLoadingSuccess, isDeleteRoleSuccess]);

  const handleUserAccess = (id: any) => {
    navigate(`/user-management/${id}/user-role-access-control`, { state: id });
  };

  const handleRowSelection = (event: any, params: any) => {
    if (selectedRows.includes(params)) {
      setSelectedRows(selectedRows.filter((row: any) => row !== params));
    } else {
      setSelectedRows([...selectedRows, params]);
    }
  };

  const columns: any = [
    {
      sortable: false,
      renderCell: (params: any) => {
        const disableDelete = !params.row.is_allowed || params.row.assigned;
        return (
          <CheckboxComponent
            checked={selectedRows.includes(params.row.id)}
            handleCheckboxChange={(event: any) => handleRowSelection(event, params.row.id)}
            disabled={disableDelete}
          />
        );
      },
    },
    {
      field: 'name',
      headerName: 'Roles',
      flex: 1,
      resizable: false,
      customFilterReq: true,
      headerAlign: 'left',
      align: 'left',
      minWidth: 100,
      renderCell: (params: any) => {
        const isNotAllowed = !params.row.is_allowed;
        const disableUpdate = !params.row.is_allowed || !userAccess.update;
        return (
          <Typography variant='body2' component='span' className={isNotAllowed ? 'disabled' : ''} sx={Styles.cellText}>
            {params.value}
          </Typography>
        );
      },
    },
    {
      field: 'actions',
      headerName: 'ACTIONS',
      sortable: false,
      headerAlign: 'center',
      align: 'center',
      customFilterReq: false,
      width: 200,
      renderCell: (params: any) => {
        const disableDelete =
          !params.row.is_allowed || params.row.assigned || !userAccess.delete || !userListAccess.read;

        const disableUpdate = !params.row.is_allowed || !userAccess.update;
        return (
          <>
            <IconButton
              sx={{
                '&.MuiIconButton-root.Mui-disabled': {
                  '.MuiSvgIcon-root': {
                    color: palette.disabled.light,
                  },
                },
              }}
              onClick={() => handleMultipleActions(params.row.id, 'delete')}
              disabled={disableDelete}
            >
              <DeleteIcon
                style={{
                  cursor: 'pointer',
                  width: 24,
                  height: 24,
                  opacity: disableDelete ? 0.5 : 1,
                  color: themeData.palette.custom.light,
                }}
              />
            </IconButton>
            <IconButton onClick={() => handleUserAccess(params.row.id)} disabled={disableUpdate}>
              <Accountkey style={{ cursor: 'pointer', width: 24, height: 24, opacity: disableUpdate ? 0.5 : 1, color: themeData.palette.custom.light, }} />
            </IconButton>
          </>
        );
      },
    },
  ];

  const closeModal = () => {
    setAddPopupOpen(false);
    setUserRoleData(userFormData);
  };

  const handleMultipleActions = (payload: any, type: string) => {
    if (type === 'delete') {
      setUserRoleData(payload);
      setRoleIds([payload]);
      setConfirmPopup(true);
    } else if (type == 'warning') {
      setUserRoleData(userFormData);
      setConfirmPopup(false);
    } else if (type == 'success') {
      dispatch(userRoleActions.resetLoadingState());
    } else if (type == 'confirm') {
      dispatch(userRoleActions.deleteMultipleRoleRequest(roleIds));
      setSelectedRows([]);
      setUserRoleData(userFormData);
      setConfirmPopup(false);
    }
  };

  const renderModal = () => (
    <AddRole
      isOpen={addPopupOpen}
      userRoleData={userRoleData}
      setUserRoleData={setUserRoleData}
      handleClose={closeModal}
    />
  );

  return (
    <>
      <DataTable
        rows={userRole}
        columns={columns}
        isServerPagination
        totalRowCount={userRoleTotalRowCount}
        pageNumber={userRolePageNumber}
        pageSize={10}
        onPagination={(pageSize: number, pageNumber: number) => {
          dispatchRolesRequest({ limit: pageSize, page: pageNumber });
        }}
        onSorting={({ field, sort }: { field: string; sort: string }) => {
          const sortModel = { field, sort };
          dispatchRolesRequest({
            limit: DEFAULT_PAGE_SIZE,
            page: userRolePageNumber,
            ...sortModel,
          });
          setSortingModel(sortModel);
        }}
        loading={isUserRoleLoading}
      />
      <PopUp
        open={confirmPopup}
        icon={<AlertIcon color={palette.warning.main} />}
        message='Are You Sure to Delete this Role?'
        cancel={true}
        confirmation={true}
        cancelLabel='No'
        confirmLabel='Yes'
        onConfirm={() => handleMultipleActions({}, 'confirm')}
        onClose={() => handleMultipleActions({}, 'warning')}
        onCancel={() => handleMultipleActions({}, 'warning')}
      />
      {renderModal()}
    </>
  );
};

const mapStateToProps = (state: any) => ({
  userRole: state.userRole.userRole,
  isUserRoleLoading: state.userRole.isUserRoleLoading,
  isAddRoleLoadingSuccess: state.userRole.isAddRoleLoadingSuccess,
  isDeleteRoleSuccess: state.userRole.isDeleteRoleSuccess,
  isDeleteRoleError: state.userRole.isDeleteRoleError,
  userRoleError: state.userRole.userRoleError,
  userRoleManagementMessage: state.userRole.userRoleManagementMessage,
  isAddRoleError: state.userRole.isAddRoleError,
  userRoleTotalRowCount: state.userRole.userRoleTotalRowCount,
  userRolePageNumber: state.userRole.userRolePageNumber,
});

export default connect(mapStateToProps)(UserRole);
